import { useCallback, useEffect, useRef, useState } from 'react';
export const defaultInboxFilters = {
  scope: 'active',
  read: 'unread',
  query: '',
  workgroup: '',
};
export const inboxTitle = (item) =>
  item.address ||
  item.body
    ?.split('\n')
    .find((line) => line.trim())
    ?.slice(0, 120) ||
  item.channel_name ||
  item.space_name;
export const inboxPreview = (item) =>
  (item.preview || 'Photos or attachment')
    .replace(/<@\d+>/g, '@member')
    .replace(/<!channel>/g, '@channel');
export const inboxSource = (item) =>
  `${item.source_kind === 'WORKER' ? 'Worker' : 'Customer'} · ${[...new Set([item.source_name, item.space_name].filter(Boolean))].join(' · ')}`;
const emptyPage = { threads: [], unread_count: 0, has_more: false };
/** One request per page, including metadata and counts. No per-row fetches. */
export function useWorkInbox(
  api,
  filters,
  revision,
  selectedId,
  enabled = true,
) {
  const [page, setPage] = useState(emptyPage);
  const [loading, setLoading] = useState(true),
    [loadingMore, setLoadingMore] = useState(false),
    [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const state = useRef({ page, key: '', selectedId });
  const request = useRef(undefined),
    paging = useRef(false);
  const key = JSON.stringify([
    filters.scope,
    filters.read,
    filters.query.trim(),
    filters.workgroup,
  ]);
  useEffect(() => {
    state.current.page = page;
    state.current.selectedId = selectedId;
  }, [page, selectedId]);
  const path = useCallback(
    (cursor) => {
      const params = new URLSearchParams({
        sort: 'activity',
        limit: '50',
        scope: filters.scope,
        read: filters.read,
      });
      if (filters.query.trim()) params.set('q', filters.query.trim());
      if (filters.workgroup) params.set('workgroup_id', filters.workgroup);
      if (cursor) params.set('cursor', cursor);
      return `/work-inbox?${params}`;
    },
    [filters.scope, filters.read, filters.query, filters.workgroup],
  );
  const refresh = useCallback(() => setRetry((value) => value + 1), []);
  useEffect(() => {
    request.current?.abort();
    if (!enabled) {
      setPage(emptyPage);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    request.current = controller;
    paging.current = false;
    const previous = state.current.key === key ? state.current.page : emptyPage;
    state.current.key = key;
    setPage(previous);
    setLoading(true);
    setLoadingMore(false);
    setError('');
    const timer = setTimeout(
      () => {
        void (async () => {
          let incoming = await api(path(), 'GET', undefined, controller.signal);
          const seen = new Set();
          const pages = Math.max(1, Math.ceil(previous.threads.length / 50));
          for (
            let i = 1;
            i < pages &&
            incoming.has_more &&
            incoming.next_cursor &&
            !controller.signal.aborted;
            i++
          ) {
            if (seen.has(incoming.next_cursor)) break;
            seen.add(incoming.next_cursor);
            const next = await api(
              path(incoming.next_cursor),
              'GET',
              undefined,
              controller.signal,
            );
            incoming = {
              ...next,
              threads: [...incoming.threads, ...next.threads],
            };
          }
          if (controller.signal.aborted) return;
          const items = [
            ...new Map(
              incoming.threads.map((item) => [item.id, item]),
            ).values(),
          ];
          const selected = previous.threads.find(
            (item) => item.id === state.current.selectedId,
          );
          // Keep the item being read in place until the user leaves its detail.
          if (
            selected &&
            filters.read === 'unread' &&
            !items.some((item) => item.id === selected.id)
          ) {
            items.splice(
              Math.min(previous.threads.indexOf(selected), items.length),
              0,
              { ...selected, unread: false },
            );
          }
          setPage({ ...incoming, threads: items });
        })()
          .catch((cause) => {
            if (!controller.signal.aborted) {
              if ([401, 403].includes(cause.status)) setPage(emptyPage);
              setError(
                cause instanceof Error ? cause.message : 'Unable to load inbox',
              );
            }
          })
          .finally(() => {
            if (!controller.signal.aborted) setLoading(false);
          });
      },
      filters.query.trim() ? 250 : 120,
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, key, path, revision, retry, enabled, filters.read, filters.query]);
  const loadMore = useCallback(async () => {
    const current = state.current.page,
      controller = request.current;
    if (
      !controller ||
      controller.signal.aborted ||
      paging.current ||
      loading ||
      !current.has_more ||
      !current.next_cursor
    )
      return;
    paging.current = true;
    setLoadingMore(true);
    setError('');
    try {
      const next = await api(
        path(current.next_cursor),
        'GET',
        undefined,
        controller.signal,
      );
      if (!controller.signal.aborted)
        setPage((value) => ({
          ...next,
          threads: [
            ...new Map(
              [...value.threads, ...next.threads].map((item) => [
                item.id,
                item,
              ]),
            ).values(),
          ],
        }));
    } catch (cause) {
      if (!controller.signal.aborted)
        setError(
          cause instanceof Error
            ? cause.message
            : 'Unable to load more messages',
        );
    } finally {
      if (request.current === controller) {
        paging.current = false;
        setLoadingMore(false);
      }
    }
  }, [api, path, loading]);
  useEffect(() => {
    if (filters.read === 'unread')
      setPage((value) => ({
        ...value,
        threads: value.threads.filter(
          (item) => item.unread || item.id === selectedId,
        ),
      }));
  }, [selectedId, filters.read]);
  const remove = useCallback(
    (id) => {
      setPage((value) => ({
        ...value,
        threads: value.threads.filter((item) => item.id !== id),
      }));
      refresh();
    },
    [refresh],
  );
  return { page, loading, loadingMore, error, refresh, loadMore, remove };
}
export function useInboxGroups(api, admin, revision = 0) {
  const [groups, setGroups] = useState([]),
    [error, setError] = useState('');
  useEffect(() => {
    if (!admin) {
      setGroups([]);
      return;
    }
    const abort = new AbortController();
    void (async () => {
      const all = [];
      let after = 0;
      for (;;) {
        const result = await api(
          `/workgroups?limit=100&after_id=${after}`,
          'GET',
          undefined,
          abort.signal,
        );
        all.push(...result.workgroups);
        const last = result.workgroups.at(-1)?.id || 0;
        if (result.workgroups.length < 100 || last <= after) break;
        after = last;
      }
      if (!abort.signal.aborted) {
        setGroups(all);
        setError('');
      }
    })().catch((cause) => {
      if (!abort.signal.aborted)
        setError(
          cause instanceof Error ? cause.message : 'Unable to load workgroups',
        );
    });
    return () => abort.abort();
  }, [api, admin, revision]);
  return { groups, error };
}
